import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicWatch, ApiError } from './api';
import { HlsPlayer } from './hls-player';

type EmbedState =
  | { kind: 'loading' }
  | { kind: 'ready'; hlsUrl: string; thumbnailUrl?: string; captionUrl?: string }
  | { kind: 'unavailable' };

/**
 * The one route meant to be framed by arbitrary external sites (LMS pages) — no header, no nav,
 * just the player. Reuses the same public /watch lookup as the full watch page; CloudFront gives
 * this path (and only this path) a response-headers policy without X-Frame-Options — see
 * hosting-stack.ts.
 */
export function EmbedPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<EmbedState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) {
        setState({ kind: 'unavailable' });
        return;
      }
      try {
        const payload = await fetchPublicWatch(id);
        if (cancelled) return;
        if (!payload) setState({ kind: 'unavailable' });
        else
          setState({
            kind: 'ready',
            hlsUrl: payload.hlsUrl,
            thumbnailUrl: payload.thumbnailUrl,
            captionUrl: payload.captionUrl,
          });
      } catch (e) {
        if (cancelled) return;
        setState({ kind: 'unavailable' });
        void (e as ApiError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.kind === 'loading') return null;
  if (state.kind === 'unavailable') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <p className="muted">This video is not available.</p>
      </div>
    );
  }
  // Fill whatever box the embedder sized the iframe to — HlsPlayer's default `maxHeight: 70vh`
  // is meant for the full watch/detail page and would otherwise clamp the video below its
  // natural aspect-ratio height inside a small iframe. `object-fit: contain` letterboxes instead
  // of cropping or distorting if the iframe's aspect ratio doesn't match the video's.
  return (
    <HlsPlayer
      src={state.hlsUrl}
      poster={state.thumbnailUrl}
      style={{ width: '100%', height: '100%', maxHeight: 'none', objectFit: 'contain' }}
      playOverlay
      captionUrl={state.captionUrl}
    />
  );
}
